import React, { useState } from 'react';
import { X, Check, ArrowRight, ArrowLeft, Upload, FileText, Loader2, Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

// Utility to capitalize first letter of each word
const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
};

const OnboardingWizard = ({ onComplete, onBack, onLoginInstead }) => {
    const [step, setStep] = useState(0);
    // Steps: 
    // 0: Name & Role
    // 1: Email (domain validation + duplicate check)
    // 2: Password
    // 3: School Context (Subject/Grade)
    // 4: Syllabus Upload
    // 5: Processing
    // 6: Success

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        subject: '',
        grade: '',
        role: 'Teacher',
        files: []
    });

    const [showPassword, setShowPassword] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});

    // Password strength validation
    const hasMinLength = formData.password.length >= 8;
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const passwordsMatch = formData.password === formData.confirmPassword && formData.password.length > 0;

    // Checks if current step is valid to proceed
    const isValid = () => {
        if (step === 0) return formData.name.length > 2;
        if (step === 1) return formData.email.includes('@');
        if (step === 2) return hasMinLength && passwordsMatch;
        if (step === 3) return formData.subject.length > 2 && formData.grade.length > 0;
        if (step === 4) return true; // File upload is optional
        return true;
    };

    const handleNext = async () => {
        if (!isValid()) return;

        // Email step — validate domain + check duplicates
        if (step === 1) {
            setCheckingEmail(true);
            setEmailError('');
            try {
                const res = await api.post('/validate-signup-email', { email: formData.email });
                if (!res.data.valid) {
                    setEmailError(res.data.error);
                    setCheckingEmail(false);
                    return;
                }
            } catch (err) {
                console.warn('Email validation failed:', err);
                // Don't block signup if the validation endpoint is unreachable
            }
            setCheckingEmail(false);
        }

        // If last step before processing, submit everything
        if (step === 4) {
            await handleSubmit();
        } else {
            setStep(prev => prev + 1);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFormData(prev => ({ ...prev, files: [...prev.files, ...Array.from(e.target.files)] }));
        }
    };

    const handleSubmit = async () => {
        setStep(5); // Processing screen
        setIsUploading(true);

        try {
            // 1. Register user with password (returns JWT for auto-login)
            const registerResponse = await api.post('/register', {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                subject: formData.subject,
                grade: formData.grade,
                role: formData.role
            });

            // Store token and profile for auto-login
            const profile = registerResponse.data;
            if (profile.token) {
                localStorage.setItem('saige_token', profile.token);
            }
            localStorage.setItem('saige_profile', JSON.stringify(profile));
            localStorage.setItem('saige_onboarding_complete', 'true');

            // 2. Upload files if any (using teacher-context endpoint so they appear in Settings)
            if (formData.files.length > 0) {
                const errors = [];
                for (let i = 0; i < formData.files.length; i++) {
                    const file = formData.files[i];
                    const payload = new FormData();
                    payload.append('file', file);
                    payload.append('doc_type', 'syllabus');
                    payload.append('title', file.name);
                    if (formData.subject) payload.append('subject', formData.subject);
                    if (formData.grade) payload.append('grade_level', formData.grade);
                    try {
                        await api.post('/api/teacher-context/upload', payload, {
                            onUploadProgress: (progressEvent) => {
                                const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                                setUploadProgress(prev => ({ ...prev, [file.name]: pct }));
                            }
                        });
                        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
                    } catch (uploadErr) {
                        console.warn(`Failed to upload ${file.name}:`, uploadErr);
                        errors.push(file.name);
                        // Don't block onboarding if a file upload fails
                    }
                }
                if (errors.length > 0) {
                    setUploadErrors(errors);
                }
            }

            // 3. Complete onboarding — verification email sent in background by backend
            setStep(6);
            setTimeout(() => {
                onComplete(profile);
            }, 1500);

        } catch (error) {
            console.error("Onboarding API error:", error.response?.status, error.response?.data, error);
            const backendMsg = error.response?.data?.detail;
            if (error.response?.status === 409) {
                alert("An account with this email already exists. Please log in instead.");
            } else {
                alert(backendMsg || "Registration failed. Please try again or contact support.");
            }
            setStep(4); // Go back to file upload step
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6 bg-white">

            {/* Navigation Controls */}
            <button onClick={onBack} className="absolute top-8 left-8 text-slate-400 hover:text-slate-600 z-50">
                <X size={24} />
            </button>

            <div className="w-full max-w-xl relative z-10">
                {/* Progress Bar */}
                {step < 5 && (
                    <div className="w-full h-1 bg-slate-100 rounded-full mb-12 overflow-hidden">
                        <motion.div
                            className="h-full bg-[#FFC107]"
                            initial={{ width: 0 }}
                            animate={{ width: `${((step + 1) / 5) * 100}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                )}

                <AnimatePresence mode='wait'>

                    {/* Step 0: Name */}
                    {step === 0 && (
                        <motion.div
                            key="step0"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-8"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Let's get introduced.<br />What's your name?</h2>
                            <input
                                type="text"
                                placeholder="Type your name..."
                                className="w-full text-3xl md:text-4xl border-b-2 border-slate-200 py-4 focus:border-[#FFC107] focus:outline-none bg-transparent text-slate-800 placeholder-slate-300 transition-colors"
                                autoFocus
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                onBlur={e => setFormData({ ...formData, name: capitalizeWords(e.target.value) })}
                                onKeyDown={e => e.key === 'Enter' && handleNext()}
                            />

                            <div className="pt-4">
                                <p className="text-sm text-slate-500 mb-3">What's your role?</p>
                                <div className="flex flex-wrap gap-2">
                                    {['Teacher', 'Paraprofessional', 'Support Staff', 'Administrator'].map(role => (
                                        <button
                                            key={role}
                                            onClick={() => setFormData({ ...formData, role })}
                                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${formData.role === role
                                                ? 'bg-amber-500 text-white shadow-md'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                        >
                                            {role}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 1: Email */}
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-8"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Nice to meet you, {formData.name.split(' ')[0]}.<br />What's your email?</h2>
                            <input
                                type="email"
                                placeholder="name@email.com"
                                className={`w-full text-3xl md:text-4xl border-b-2 py-4 focus:outline-none bg-transparent text-slate-800 placeholder-slate-300 transition-colors ${emailError ? 'border-red-400 focus:border-red-400' : 'border-slate-200 focus:border-[#FFC107]'
                                    }`}
                                autoFocus
                                value={formData.email}
                                onChange={e => { setFormData({ ...formData, email: e.target.value }); setEmailError(''); }}
                                onKeyDown={e => e.key === 'Enter' && handleNext()}
                            />
                            {emailError && (
                                <p className="text-red-500 text-sm mt-2">
                                    {emailError}{' '}
                                    {emailError.includes('already has an account') && (
                                        <button
                                            type="button"
                                            onClick={onLoginInstead || onBack}
                                            className="text-amber-600 hover:text-amber-700 font-semibold underline"
                                        >
                                            Log in instead
                                        </button>
                                    )}
                                </p>
                            )}
                            {checkingEmail && (
                                <p className="text-slate-400 text-sm mt-2 flex items-center gap-2">
                                    <Loader2 size={14} className="animate-spin" /> Checking email…
                                </p>
                            )}
                        </motion.div>
                    )}

                    {/* Step 2: Password */}
                    {step === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-6"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Create a password</h2>
                            <p className="text-xl text-slate-500">You'll use this to log in to Saige</p>

                            {/* Password input */}
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                                    <Lock size={20} />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Create a password"
                                    className="w-full pl-12 pr-12 py-4 text-xl border-2 border-slate-200 rounded-xl focus:border-[#FFC107] focus:outline-none bg-transparent text-slate-800 placeholder-slate-300 transition-colors"
                                    autoFocus
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            {/* Password strength */}
                            <div className="space-y-2 p-4 bg-slate-50 rounded-xl">
                                <div className={`flex items-center gap-2 text-sm ${hasMinLength ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${hasMinLength ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                        {hasMinLength && <CheckCircle size={10} className="text-emerald-600" />}
                                    </div>
                                    At least 8 characters
                                </div>
                                <div className={`flex items-center gap-2 text-sm ${hasUppercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${hasUppercase ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                        {hasUppercase && <CheckCircle size={10} className="text-emerald-600" />}
                                    </div>
                                    One uppercase letter
                                </div>
                                <div className={`flex items-center gap-2 text-sm ${hasNumber ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${hasNumber ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                        {hasNumber && <CheckCircle size={10} className="text-emerald-600" />}
                                    </div>
                                    One number
                                </div>
                            </div>

                            {/* Confirm password */}
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                                    <Lock size={20} />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Confirm password"
                                    className={`w-full pl-12 pr-4 py-4 text-xl border-2 rounded-xl focus:outline-none bg-transparent text-slate-800 placeholder-slate-300 transition-colors
                                        ${formData.confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-slate-200 focus:border-[#FFC107]'}
                                        ${passwordsMatch ? 'border-emerald-300' : ''}`}
                                    value={formData.confirmPassword}
                                    onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    onKeyDown={e => e.key === 'Enter' && handleNext()}
                                />
                            </div>
                            {formData.confirmPassword && !passwordsMatch && (
                                <p className="text-red-500 text-sm">Passwords don't match</p>
                            )}
                        </motion.div>
                    )}

                    {/* Step 3: Context */}
                    {step === 3 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-8"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">What do you teach?</h2>
                            <div className="space-y-4">
                                <input
                                    type="text"
                                    placeholder="Subject (e.g. AP US History)"
                                    className="w-full text-2xl border-b-2 border-slate-200 py-4 focus:border-[#FFC107] focus:outline-none bg-transparent text-slate-800 placeholder-slate-300"
                                    autoFocus
                                    value={formData.subject}
                                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                />
                                <input
                                    type="text"
                                    placeholder="Grade Level (e.g. 11th Grade)"
                                    className="w-full text-2xl border-b-2 border-slate-200 py-4 focus:border-[#FFC107] focus:outline-none bg-transparent text-slate-800 placeholder-slate-300"
                                    value={formData.grade}
                                    onChange={e => setFormData({ ...formData, grade: e.target.value })}
                                    onKeyDown={e => e.key === 'Enter' && handleNext()}
                                />
                                <p className="text-sm text-slate-400">You can add more subjects later in Settings.</p>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 4: File Upload */}
                    {step === 4 && (
                        <motion.div
                            key="step4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-8"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Share Your Resources <span className="text-lg font-normal text-slate-400">(Optional)</span></h2>
                            <p className="text-xl text-slate-500">
                                Upload your Syllabus, Bell Schedule, or any PDF so Saige can learn your style.
                                <span className="block text-base text-slate-400 mt-2">You can always add resources later in Settings.</span>
                            </p>

                            <div className="border-2 border-dashed border-slate-300 rounded-3xl p-12 text-center hover:bg-slate-50 transition-colors relative group">
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    onChange={handleFileChange}
                                />
                                <Upload className="mx-auto h-12 w-12 text-slate-400 mb-4 group-hover:text-[#FFC107] transition-colors" />
                                {formData.files.length > 0 ? (
                                    <div className="space-y-2">
                                        <h3 className="text-xl font-bold text-slate-800">{formData.files.length} file{formData.files.length > 1 ? 's' : ''} selected</h3>
                                        <ul className="text-sm text-slate-500">
                                            {formData.files.map((f, i) => (
                                                <li key={i} className="flex items-center justify-center gap-2">
                                                    <FileText size={12} className="text-slate-400" />
                                                    {f.name}
                                                    {uploadProgress[f.name] !== undefined && uploadProgress[f.name] < 100 && (
                                                        <span className="text-xs text-amber-500">{uploadProgress[f.name]}%</span>
                                                    )}
                                                    {uploadProgress[f.name] === 100 && (
                                                        <Check size={12} className="text-emerald-500" />
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-700">Drop files or click here</h3>
                                        <p className="text-slate-400 mt-2">PDF, Word, Excel, PowerPoint, or images</p>
                                    </div>
                                )}
                            </div>

                            {/* Upload errors */}
                            {uploadErrors.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">
                                    <p className="font-medium">Some files couldn't be uploaded:</p>
                                    <ul className="mt-1 list-disc list-inside">
                                        {uploadErrors.map((name, i) => <li key={i}>{name}</li>)}
                                    </ul>
                                    <p className="mt-1 text-red-400">You can re-upload them later in Settings.</p>
                                </div>
                            )}

                            {/* Skip button — always visible */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNext();
                                }}
                                className="text-slate-400 hover:text-slate-600 text-sm underline transition-colors"
                            >
                                Skip for now — I'll add files later
                            </button>
                        </motion.div>
                    )}

                    {/* Step 5: Loading */}
                    {step === 5 && (
                        <motion.div
                            key="step5"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center space-y-6"
                        >
                            <div className="w-20 h-20 bg-[#FFC107] rounded-2xl flex items-center justify-center text-white mx-auto shadow-xl animate-spin-slow">
                                <Loader2 size={40} className="animate-spin" />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900">Setting up your account...</h2>
                            <p className="text-slate-500">Creating your personalized Saige experience</p>
                        </motion.div>
                    )}

                    {/* Step 6: Success */}
                    {step === 6 && (
                        <motion.div
                            key="step6"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center space-y-6"
                        >
                            <div className="w-20 h-20 bg-emerald-500 rounded-2xl flex items-center justify-center text-white mx-auto shadow-xl">
                                <Check size={40} />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900">Welcome to Saige!</h2>
                            <p className="text-xl text-slate-600">
                                Taking you to your dashboard...
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Next Button */}
                {step < 5 && (
                    <motion.div
                        className="mt-12 flex justify-end"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <button
                            onClick={handleNext}
                            disabled={!isValid()}
                            className={`px-8 py-4 rounded-2xl font-bold text-lg flex items-center gap-3 shadow-lg transition-all
                            ${isValid()
                                    ? 'bg-slate-900 text-white hover:bg-slate-800 transform hover:-translate-y-1'
                                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'}
                        `}
                        >
                            {step === 4 ? "Complete Setup" : "Continue"} {step === 4 ? <Check size={20} /> : <ArrowRight size={20} />}
                        </button>
                    </motion.div>
                )}

            </div>
        </div>
    );
};

export default OnboardingWizard;
